
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, Phone, MessageCircle, Book, HelpCircle, Bug, Lightbulb } from 'lucide-react';

const Support = () => {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/auth/login');
  };

  const handleSignup = () => {
    navigate('/auth/signup');
  };

  const supportOptions = [
    {
      icon: MessageCircle,
      title: 'Live Chat Support',
      description: 'Get instant help from our support team',
      action: 'Chat with Support',
      href: 'mailto:oss@thematmonster.com?subject=Support Request',
      primary: true
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Send us a detailed message and we\'ll respond within 24 hours',
      action: 'Email Us',
      href: 'mailto:oss@thematmonster.com?subject=Support Request'
    },
    {
      icon: Phone,
      title: 'Phone Support',
      description: 'Speak directly with our support team',
      action: 'Call Us',
      href: 'tel:+15128173552'
    }
  ];

  const helpCategories = [
    {
      icon: Book,
      title: 'Getting Started',
      description: 'Learn the basics of using The Mat Monster',
      topics: [
        'Creating your account',
        'Setting up your profile',
        'Navigating the app',
        'Understanding premium features'
      ]
    },
    {
      icon: HelpCircle,
      title: 'Account & Billing',
      description: 'Manage your subscription and account settings',
      topics: [
        'Subscription plans and pricing',
        'Payment and billing issues',
        'Account settings',
        'Cancellation and refunds'
      ]
    },
    {
      icon: Bug,
      title: 'Technical Issues',
      description: 'Troubleshoot common technical problems',
      topics: [
        'App not loading or crashing',
        'Sync issues',
        'Video playback problems',
        'Mobile app installation'
      ]
    },
    {
      icon: Lightbulb,
      title: 'Feature Requests',
      description: 'Suggest new features or improvements',
      topics: [
        'Request new techniques',
        'Suggest app improvements',
        'Report missing content',
        'Share feedback'
      ]
    }
  ];

  const faqs = [
    {
      question: 'How do I access premium features?',
      answer: 'Premium features are available with a paid subscription. You can upgrade from the subscription page in your account settings.'
    },
    {
      question: 'Can I use the app offline?',
      answer: 'Yes! The Mat Monster is designed to work offline. Once you\'ve loaded content while online, you can access it without an internet connection.'
    },
    {
      question: 'How do I sync my data across devices?',
      answer: 'Your data automatically syncs when you\'re connected to the internet. Make sure you\'re logged into the same account on all devices.'
    },
    {
      question: 'What if I forget my password?',
      answer: 'You can reset your password from the login page by clicking "Forgot Password" and following the instructions sent to your email.'
    },
    {
      question: 'How do I cancel my subscription?',
      answer: 'You can manage your subscription from the subscription page in your account settings, or contact our support team for assistance.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation onLogin={handleLogin} onSignup={handleSignup} />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold mb-4 text-primary">Support Center</h1>
            <p className="text-primary/80 max-w-2xl mx-auto text-lg">
              Get help with The Mat Monster. Our support team is here to assist you with any questions or issues.
            </p>
          </div>

          {/* Contact Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {supportOptions.map((option, index) => (
              <Card key={index} className={`transition-transform hover:scale-105 ${option.primary ? 'ring-2 ring-primary' : ''}`}>
                <CardHeader className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <option.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{option.title}</CardTitle>
                  <CardDescription>{option.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Button 
                    asChild 
                    variant={option.primary ? "default" : "outline"}
                    className="w-full"
                  >
                    <a href={option.href}>{option.action}</a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Help Categories */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-8">Help Categories</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {helpCategories.map((category, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <category.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{category.title}</CardTitle>
                        <CardDescription>{category.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {category.topics.map((topic, topicIndex) => (
                        <li key={topicIndex} className="text-sm text-muted-foreground flex items-center">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full mr-3 flex-shrink-0" />
                          {topic}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4 max-w-4xl mx-auto">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="text-center bg-muted/50 rounded-lg p-8">
            <h3 className="text-xl font-semibold mb-4">Still Need Help?</h3>
            <p className="text-muted-foreground mb-6">
              Our support team is available to help you with any questions or issues.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span className="text-sm">oss@thematmonster.com</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <span className="text-sm">(512) 817-3552</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Support;
